import {
  checkItemInCartItemTree,
  findMenuItemOrModGroupUsingPathToItem,
  isUnavailableItem,
  TopLevelMenuItem,
} from '../../../utils/menu';
import { convertToMap } from '../../../utils';
import { iterateModGroups } from '../../../utils/autocomplete';
import { CartItem } from '../../../utils/cart';
import { ParsedMenuItem, ParsedModifierGroup } from '../../../types/menu';
import { ModSymbolCodeNameMappingType } from '../../../utils/mappings';

export const processModifierSelection = ({
  cartItem,
  menuItem,
  modSymbolMapping,
  inputModSymbol,
  selectedModGroup,
  navigationLevel,
  parentModGroupId,
}: {
  cartItem: CartItem;
  menuItem: ParsedMenuItem;
  modSymbolMapping: ModSymbolCodeNameMappingType;
  inputModSymbol: string;
  selectedModGroup?: ParsedModifierGroup;
  navigationLevel?: string;
  parentModGroupId?: string;
}) => {
  let isChecked = true;
  let modGroup = undefined;

  const pathToItem = menuItem.pathToItem;

  if (pathToItem) {
    const pathIncludeItem = [pathToItem, menuItem.itemId].join('__');

    let node = checkItemInCartItemTree({
      cartItem,
      pathToItem: pathIncludeItem,
      fromSelectModifier: true,
    });

    if (
      node &&
      (inputModSymbol === '.' ||
        [inputModSymbol, modSymbolMapping[inputModSymbol]?.code].includes(
          node.modcode
        ))
    ) {
      //Modifier already exists so should be unselected
      isChecked = false;
    }
    modGroup = findMenuItemOrModGroupUsingPathToItem({ cartItem, pathToItem });
  } else {
    let groupsToCheck = selectedModGroup
      ? [selectedModGroup]
      : Object.values(cartItem.modifierGroups);

    let childrenMods = selectedModGroup
      ? convertToMap(
          Object.values(cartItem.childModifierGroups).filter(
            (childModGroup) => childModGroup.id === selectedModGroup.id
          )
        )
      : cartItem?.childModifierGroups;

    // For nested navigation, need to look deeper into the cart structure
    if (navigationLevel === 'nested' && parentModGroupId && selectedModGroup) {
      // Find the parent modifier group's selected items
      const parentChildModGroup =
        cartItem.childModifierGroups[parentModGroupId];
      if (parentChildModGroup) {
        // Look through selected items in the parent modifier group
        const selectedItems = Object.values(parentChildModGroup.selectedItems);
        for (const selectedItem of selectedItems) {
          // Check if this selected item has the modifier group we're looking for
          if (selectedItem.childModifierGroups[selectedModGroup.id]) {
            childrenMods = convertToMap([
              selectedItem.childModifierGroups[selectedModGroup.id],
            ]);
            break;
          }
        }
      }
    }

    isChecked = iterateModGroups(
      childrenMods,
      groupsToCheck,
      menuItem,
      true,
      inputModSymbol
    );

    modGroup = groupsToCheck.find((mg) =>
      Object.values(mg.menuItems).find(
        (modifier) =>
          modifier.id === menuItem.id &&
          menuItem.parentModifierGroupId === mg.id
      )
    );
  }
  return { isChecked, modGroup };
};

export const sortResults = (
  resultList: (TopLevelMenuItem | ParsedMenuItem)[],
  isUnifiedMenuEnabled: boolean
) => {
  return resultList.sort((itemA, itemB) => {
    if (!isUnifiedMenuEnabled) {
      // PRP menu: unavailable items go last
      return isUnavailableItem(itemA) - isUnavailableItem(itemB);
    }

    // Brand menu logic: unavailable items go last, but if both are unavailable, sort by pathToItem
    const unavailableA = isUnavailableItem(itemA);
    const unavailableB = isUnavailableItem(itemB);

    if (unavailableA !== unavailableB) {
      return unavailableA - unavailableB;
    }

    // If both are unavailable, or both are available, sort by pathToItem if present
    const pathA = (itemA as TopLevelMenuItem | ParsedMenuItem).pathToItem || '';
    const pathB = (itemB as TopLevelMenuItem | ParsedMenuItem).pathToItem || '';

    // If both have pathToItem, sort lexicographically
    if (pathA && pathB) {
      if (pathA < pathB) return -1;
      if (pathA > pathB) return 1;
      return 0;
    }

    return 0;
  });
};
